import React, { useEffect } from 'react'
import { Container, Box, ThemeProvider } from '@mui/material';
import {
    BrowserRouter as Router, Routes, Route, Outlet,
    useLocation, useNavigationType, createRoutesFromChildren, matchRoutes
} from "react-router-dom";
import * as Sentry from '@sentry/react';
import App from './components/App';
import Users from './components/Users';
import User from './components/User';
import Roles from './components/Roles';
import Organizations from './components/Organizations';
import SigninOidc from './components/SigninOidc';
import SilentRenew from './components/SilentRenew';
import { lightTheme, darkTheme } from '@lp/lp-ui'
import Header from './components/Header';
import Footer from './components/Footer';
import { GlobalErrorProvider, showGlobalError } from './components/GlobalErrorProvider';
import BulkProgressToast from './components/Users/BulkProgressToast';
import './index.css';
import authService from './services/AuthService';
import { currentUserUnloaded, getCurrentUserData } from './app/currentUserSlice';
import { useAppDispatch } from './app/hooks';
import { persistor } from './app/store';
import { allOperationsCleared } from './app/bulkProgressSlice';
import { usersDataReset } from './app/usersSlice';
import { cacheReset } from './app/cacheSlice';
import { REACT_APP_SENTRY_DSN, REACT_APP_SENTRY_ENVIRONMENT } from './constants';

Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    enabled: !!REACT_APP_SENTRY_DSN,           // no-op when DSN is blank (local/dev)
    environment: REACT_APP_SENTRY_ENVIRONMENT,
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
    ],
    tracesSampleRate: 0.1,
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Main: React.FC = () => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            event.preventDefault();
            const err = event.reason;
            const message = err?.message ?? String(err);
            // Ignore auth-related redirects handled elsewhere
            if (message === 'Failed to fetch' || message.includes('network')) {
                showGlobalError(
                    'Unable to connect to the server. Please check your network connection and try again.',
                    err?.stack ?? String(err)
                );
            } else {
                showGlobalError(
                    'An unexpected error occurred. Please try again or contact support if the problem persists.',
                    err?.stack ?? String(err)
                );
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        authService.getUser()
            .then(user => {
                if (user !== null) {
                    Sentry.setUser({ id: user.profile.sub, username: user.profile.name ?? user.profile.email });
                    dispatch(getCurrentUserData());
                }
            });

        const userLoaded = (user: import('oidc-client-ts').User) => {
            if (!authService.isLoggingOut) {
                Sentry.setUser({ id: user.profile.sub, username: user.profile.name ?? user.profile.email });
                dispatch(getCurrentUserData());
            }
        }
        const userUnloaded = () => {
            Sentry.setUser(null);
            dispatch(currentUserUnloaded());
            dispatch(allOperationsCleared());
            dispatch(usersDataReset());
            dispatch(cacheReset());
            persistor.purge();
        };

        authService.userManager.events.addUserLoaded(userLoaded);
        authService.userManager.events.addUserUnloaded(userUnloaded);

        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            authService.userManager.events.removeUserLoaded(userLoaded);
            authService.userManager.events.removeUserUnloaded(userUnloaded);
        };
    }, []);

    return (
        <GlobalErrorProvider>
            <Router>
                <SentryRoutes>
                    <Route element={<AdminLayout />}>
                        <Route path="/" element={<App />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/users/:id" element={<User />} />
                        <Route path="/roles" element={<Roles />} />
                        <Route path="/organizations" element={<Organizations />} />
                        <Route path="/signin-oidc" element={<SigninOidc />} />
                    </Route>
                    <Route path="/silent-renew" element={<SilentRenew />} />
                </SentryRoutes>
            </Router>
        </GlobalErrorProvider>
    )
}

const AdminLayout = () => (
  <ThemeProvider theme={lightTheme}>
    <Header />
    <Container
      component="div"
      maxWidth="xl"
      sx={{
        pt: 10,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Box>
          <Outlet />
        </Box>
      </Box>
      <Footer sx={{ m: 2 }} />
    </Container>
    <BulkProgressToast />
  </ThemeProvider>
);

export default Main;
